
import { TipCard } from "@/components/TipCard";
import { TipSkeleton } from "@/components/screen/TipSkeleton";
import type { Tip } from "@/types";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Mousewheel } from "swiper/modules";
import { useQueryClient } from "@tanstack/react-query";
import "swiper/css";
import "swiper/css/effect-coverflow";
import { useState } from "react";

interface TipsGridProps {
  tips: Tip[];
  isLoading?: boolean;
}

export function TipsGrid({ tips, isLoading }: TipsGridProps) {
  console.log("TipsGrid rendering with tips:", tips);
  const [activeIndex, setActiveIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const playSortAudio = () => {
    const audio = new Audio('/sfx/tick.mp3');
    audio.play();
  };

  const handleVote = async (tipId: number) => {
    console.log("Vote registered for tip:", tipId);
    // Invalidate the tips query to refresh the data
    await queryClient.invalidateQueries({ queryKey: ['approved-tips'] });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 w-full max-w-3xl mx-auto px-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="animate-pulse transform hover:scale-[1.02] transition-all duration-1000">
            <TipSkeleton />
          </div>
        ))}
      </div>
    );
  }

  if (!tips || tips.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-vaseline-white text-xl">No tips available yet. Be the first to share!</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[80vw] mx-auto px-4 h-[120vh] swipDiv">
      <Swiper
        direction="vertical"
        slidesPerView={11}
        centeredSlides={true}
        spaceBetween={-100}
        loop={true}
        loopPreventsSliding={true}
        loopAdditionalSlides={1}
        effect="coverflow"
        coverflowEffect={{
          rotate: 0,
          stretch: 0,
          depth: 30,
          modifier: 2,
          slideShadows: false,
        }}
        modules={[EffectCoverflow, Mousewheel]}
        onSlideChange={(swiper) => {
          const newIndex = swiper.realIndex;
          setActiveIndex(newIndex);

          if (newIndex !== previousIndex) {
            playSortAudio();
            setPreviousIndex(newIndex);
          }
        }}
        mousewheel={{
          forceToAxis: true,
          sensitivity: 0.0,
        }}
        touchStartPreventDefault={false}
        className="h-full"
      >
        {tips.map((tip, index) => {
          const distance = Math.abs(index - activeIndex);
          let scale = 1 - distance * 0.1;
          let opacity = 1 - distance * 0.3;

          if (distance > 5) {
            scale = 0;
            opacity = 0;
          }

          const rotateX = index < activeIndex 
            ? -Math.min(distance * 18, 60)
            : Math.min(distance * 18, 60);

          const blur = Math.min(distance * 2, 10);

          return (
            <SwiperSlide key={`${tip.id}-${index}`} onClick={(e) => e.stopPropagation()}>
              <div
                className="transition-all duration-500 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  const swiperElement = e.currentTarget.closest('.swiper') as HTMLElement;
                  if (swiperElement) {
                    const swiperInstance = (swiperElement as any).swiper;
                    if (swiperInstance) {
                      swiperInstance.slideToLoop(index);
                    }
                  }
                }}
                style={{
                  transform: `perspective(1000px) rotateX(${rotateX}deg) scale(${scale})`,
                  opacity,
                  filter: `blur(${blur}px)`,
                }}
              >
                <TipCard
                  tip={tip.content}
                  title={tip.title}
                  description={tip.description}
                  votes={0} // This is now just a placeholder since we use university-specific votes
                  tipId={tip.id}
                  tipNumber={index + 1}
                  emoji={tip.emoji}
                  onVote={() => handleVote(tip.id)}
                  unsw_votes={tip.unsw_votes}
                  usyd_votes={tip.usyd_votes}
                  uoa_votes={tip.uoa_votes}
                />
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}
